<template>
    <section class="container-fluid d-flex justify-content-center">
      <div class="container mt-5 px-0">
        <!-- Header con estadísticas resumidas -->
        <div class="row mb-4">
          <div class="col-12 d-flex justify-content-between align-items-center">
              <h2 class="display-6 text-white mb-4">
              Panel de Actividad<span class="text-primary">.</span>
              </h2>
              <button 
              class="btn btn-outline-primary btn-sm"
              @click="forceRefresh"
              :disabled="isLoading"
              >
              <i class="fas fa-sync-alt me-2" :class="{'fa-spin': isLoading}"></i>
              {{ isLoading ? 'Actualizando...' : 'Actualizar' }}
              </button>
          </div>
          <div class="col-12 mb-4">
            <div class="row g-3">
              <!-- Stats Skeleton -->
              <template v-if="!stats.length">
                <div class="col-6 col-xl-3" v-for="i in 4" :key="i">
                  <div class="stat-card">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="stat-icon skeleton"></div>
                        <div class="ms-3" style="flex: 1">
                          <div class="skeleton mb-2" style="height: 16px; width: 60%;"></div>
                          <div class="skeleton" style="height: 28px; width: 40%;"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              
              <!-- Stats Reales -->
              <div v-else class="col-6 col-xl-3" v-for="(stat, index) in stats" :key="index">
                <div class="stat-card hover-lift">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="stat-icon" :class="stat.bgColor">
                        <i :class="stat.icon"></i>
                      </div>
                      <div class="ms-3">
                        <h6 class="card-subtitle mb-1 text-muted stat-title">{{ stat.title }}</h6>
                        <h3 class="card-title mb-0 stat-value">{{ stat.value }}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Filtros mejorados -->
          <div class="col-12">
            <div class="filter-card mb-4">
              <div class="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                <div class="d-flex gap-3 flex-grow-1">
                  <div class="search-wrapper flex-grow-1">
                    <i class="fas fa-search search-icon"></i>
                    <input 
                      type="text" 
                      class="form-control search-input" 
                      placeholder="Buscar en las actividades..."
                      v-model="filters.search"
                      @input="debounceSearch"
                    >
                  </div>
                  <select 
                    v-model="filters.action" 
                    class="form-select" 
                    @change="loadActivities"
                  >
                    <option value="">Todas las acciones</option>
                    <option 
                      v-for="action in actionTypes" 
                      :key="action.value" 
                      :value="action.value"
                    >
                      {{ action.label }}
                    </option>
                  </select>
                </div>
                <div class="d-flex gap-3">
                  <div class="date-range">
                    <input 
                      type="date" 
                      v-model="filters.date_from"
                      class="form-control" 
                      @change="loadActivities"
                    >
                  </div>
                  <button 
                    class="btn btn-outline-danger btn-sm"
                    @click="clearFilters"
                    v-if="hasActiveFilters"
                  >
                    <i class="fas fa-times me-2"></i>Limpiar filtros
                  </button>
                </div>
              </div>
            </div>
  
            <!-- Timeline de actividades -->
            <div class="activity-card">
              <div class="card-body p-0">
                <!-- Estado de carga -->
                <div v-if="isLoading" class="timeline-wrapper">
                  <div v-for="i in 5" :key="i" class="timeline-item skeleton-item">
                    <div class="timeline-icon">
                      <div class="skeleton-circle skeleton"></div>
                    </div>
                    <div class="timeline-content">
                      <div class="d-flex justify-content-start align-items-start w-100">
                        <div style="flex: 1;">
                          <div class="skeleton mb-3" style="height: 24px; width: 70%;"></div>
                          <div class="d-flex gap-2">
                            <div class="skeleton" style="height: 20px; width: 120px; border-radius: 20px;"></div>
                            <div class="skeleton" style="height: 20px; width: 100px; border-radius: 20px;"></div>
                          </div>
                        </div>
                        <div class="skeleton" style="height: 20px; width: 80px;"></div>
                      </div>
                    </div>
                  </div>
                </div>
  
                <!-- Lista de actividades -->
                <div v-else class="timeline-wrapper">
                  <div 
                    v-for="(activity, index) in activities" 
                    :key="index" 
                    class="timeline-item"
                    :class="{'expanded': activity.showChanges}"
                  >
                    <div class="timeline-icon" :class="getActionColor(activity.action)">
                      <i :class="getActionIcon(activity.action)"></i>
                    </div>
                    
                    <div class="timeline-content">
                      <div class="d-flex flex-column">
                        <div class="activity-header">
                          <h6 class="mb-2 activity-title text-center text-lg-start">{{ activity.description }}</h6>
                          <div class="d-flex flex-column flex-lg-row justify-content-lg-between mt-3 align-items-center gap-2">
                            <div class="d-flex align-items-center gap-2 order-2 order-lg-1">
                              <span class="user-badge text-white fw-light">
                                <i class="fas fa-user me-1"></i>
                                {{ activity.user?.username || 'Sistema' }}
                              </span>
                              <span class="action-badge" :class="getActionColor(activity.action)">
                                {{ getActionLabel(activity.action) }}
                              </span>
                            </div>
                            <small class="text-muted timestamp order-1 order-lg-2">
                              {{ formatDate(activity.timestamp) }}
                            </small>
                          </div>
                        </div>
  
                        <!-- Detalles de cambios -->
                        <div v-if="activity.changes" class="mt-3">
                          <button 
                            class="btn btn-link btn-sm p-0 text-decoration-none changes-toggle"
                            @click="activity.showChanges = !activity.showChanges"
                          >
                            <i :class="activity.showChanges ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="me-1"></i>
                            {{ activity.showChanges ? 'Ocultar detalles' : 'Ver detalles' }}
                          </button>
                          
                          <div v-if="activity.showChanges" class="changes-details mt-2">
                            <pre class="changes-json">{{ formatChanges(activity.changes) }}</pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
  
                  <!-- Botón cargar más -->
                  <div v-if="hasMoreActivities" class="text-center p-4">
                    <button 
                      class="btn btn-outline-primary px-4"
                      @click="loadMore" 
                      :disabled="isLoadingMore"
                    >
                      <i class="fas fa-sync-alt me-2" :class="{'fa-spin': isLoadingMore}"></i>
                      {{ isLoadingMore ? 'Cargando más...' : 'Cargar más' }}
                    </button>
                  </div>
                </div>
  
                <!-- Estado sin resultados -->
                <div v-if="!isLoading && activities.length === 0" class="text-center p-5">
                  <i class="fas fa-history fa-3x text-muted mb-3"></i>
                  <p class="text-muted">No hay actividades para mostrar</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script>
  import { ref, onMounted, computed, onUnmounted } from 'vue';
  import { getActivityLogs, getActivityStats, clearActivityCache } from '@/services/activity';
  import { formatDistanceToNow } from 'date-fns';
  import { es } from 'date-fns/locale';
  import { useToast } from 'vue-toastification'; // Agregar esta importación
  
  export default {
    name: 'ActivityPage',
    setup() {
      const toast = useToast(); // Agregar esta línea
      const activities = ref([]);
      const stats = ref([]); // Inicializar como array vacío
      const isLoading = ref(true);
      const isLoadingMore = ref(false);
      const page = ref(1);
      const hasMoreActivities = ref(true);
      const filters = ref({
        action: '',
        date_from: '',
        search: ''
      });
  
      const actionTypes = [
        { value: 'create', label: 'Creaciones' },
        { value: 'update', label: 'Modificaciones' },
        { value: 'delete', label: 'Eliminaciones' },
        { value: 'register', label: 'Registros' },
        { value: 'login', label: 'Inicios de sesión' },
        { value: 'order', label: 'Órdenes' },
        { value: 'status_change', label: 'Cambios de estado' }
      ];
  
      const loadActivities = async (resetPage = true) => {
        if (resetPage) {
          page.value = 1;
          activities.value = [];
        }
        
        try {
          isLoading.value = true;
          const response = await getActivityLogs({
            ...filters.value,
            page: page.value
          });
          
          // Ajustar el manejo de la respuesta
          let results;
          if (response.results) {
            results = response.results;
            hasMoreActivities.value = response.next !== null;
          } else if (Array.isArray(response)) {
            results = response;
            hasMoreActivities.value = false;
          } else if (response.data) {
            results = response.data.results || response.data;
            hasMoreActivities.value = response.data.next !== null;
          } else {
            results = [];
            hasMoreActivities.value = false;
          }
  
          const newActivities = results.map(activity => ({
            ...activity,
            showChanges: false
          }));
          
          activities.value = resetPage 
            ? newActivities 
            : [...activities.value, ...newActivities];
  
        } catch (error) {
          console.error('Error loading activities:', error);
          toast.error('Error al cargar las actividades');
        } finally {
          isLoading.value = false;
          isLoadingMore.value = false;
        }
      };
  
      const loadStats = async () => {
        try {
          const response = await getActivityStats();
          // Simular un pequeño delay para mostrar el skeleton (opcional)
          await new Promise(resolve => setTimeout(resolve, 500));
          stats.value = [
            {
              title: 'Actividades Hoy',
              value: response.data.activities_today,
              icon: 'fas fa-calendar-day text-primary',
              bgColor: 'bg-primary'
            },
            {
              title: 'Esta Semana',
              value: response.data.activities_last_week,
              icon: 'fas fa-calendar-week text-success',
              bgColor: 'bg-success'
            },
            {
              title: 'Este Mes',
              value: response.data.activities_last_month,
              icon: 'fas fa-calendar-alt text-info',
              bgColor: 'bg-info'
            },
            {
              title: 'Total',
              value: response.data.total_activities,
              icon: 'fas fa-chart-line text-warning',
              bgColor: 'bg-warning'
            }
          ];
        } catch (error) {
          console.error('Error loading stats:', error);
          stats.value = []; // Mantener vacío en caso de error
        }
      };
  
      const loadMore = async () => {
        if (isLoadingMore.value) return;
        
        isLoadingMore.value = true;
        page.value++;
        await loadActivities(false);
      };
  
      const formatDate = (timestamp) => {
        return formatDistanceToNow(new Date(timestamp), { 
          addSuffix: true,
          locale: es 
        });
      };
  
      const getActionIcon = (action) => {
        const icons = {
          create: 'fas fa-plus',
          update: 'fas fa-edit',
          delete: 'fas fa-trash',
          register: 'fas fa-user-plus',
          login: 'fas fa-sign-in-alt',
          order: 'fas fa-shopping-cart',
          status_change: 'fas fa-exchange-alt'
        };
        return icons[action] || 'fas fa-info';
      };
  
      const getActionColor = (action) => {
        const colors = {
          create: 'bg-success',
          update: 'bg-primary',
          delete: 'bg-danger',
          register: 'bg-info',
          login: 'bg-secondary',
          order: 'bg-warning',
          status_change: 'bg-info'
        };
        return colors[action] || 'bg-secondary';
      };
  
      const getActionLabel = (action) => {
        return actionTypes.find(type => type.value === action)?.label || action;
      };
  
      const formatChanges = (changes) => {
        if (!changes) return '';
        
        const statusTranslations = {
          'pending': 'pendiente',
          'completed': 'completado',
          'cancelled': 'cancelado'
        };
  
        const fieldTranslations = {
          'name': 'Nombre',
          'brand': 'Banco',
          'price': 'Precio',
          'stock': 'Stock',
          'description': 'Descripción',
          'status': 'Estado',
          'blisters': 'Presentaciones',
          'flavors': 'Sabores',
          'effects': 'Efectos',
          'thc': 'THC',
          'cbd': 'CBD',
          'genotype': 'Genotipo',
          'flowering_time': 'Tiempo de floración',
          'height_indoor': 'Altura interior',
          'height_outdoor': 'Altura exterior',
          'yield_indoor': 'Producción interior',
          'yield_outdoor': 'Producción exterior',
          'image': 'Imagen principal',
          'image_2': 'Imagen secundaria',
          'image_3': 'Imagen adicional'
        };
        
        if (changes.before && changes.after) {
          const differences = [];
          const ignoreFields = ['updated_at', 'created_at', 'id'];
          
          // Procesar todos los campos
          Object.keys(changes.after).forEach(key => {
            if (ignoreFields.includes(key)) return;
            
            const beforeValue = changes.before[key];
            const afterValue = changes.after[key];
            
            if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
              const fieldName = fieldTranslations[key] || key;
              
              // Si es un cambio de imagen
              if (key.startsWith('image')) {
                if (!beforeValue && afterValue) {
                  differences.push(`🖼️ Se agregó ${fieldName.toLowerCase()}`);
                } else if (beforeValue && !afterValue) {
                  differences.push(`🖼️ Se eliminó ${fieldName.toLowerCase()}`);
                } else if (beforeValue !== afterValue) {
                  differences.push(`🖼️ Se actualizó ${fieldName.toLowerCase()}`);
                }
                return;
              }
  
              // Si es un cambio en blisters
              if (key === 'blisters') {
                const beforeBlisters = beforeValue || [];
                const afterBlisters = afterValue || [];
                let hasBlisterChanges = false;
                const blisterChanges = [];
                
                // Verificar si hay cambios en los blisters
                afterBlisters.forEach((newBlister, index) => {
                  const oldBlister = beforeBlisters[index];
                  const isNew = !oldBlister;
                  const hasChanged = oldBlister && (
                    oldBlister.price !== newBlister.price ||
                    oldBlister.stock !== newBlister.stock ||
                    oldBlister.seed_count !== newBlister.seed_count
                  );
                  
                  if (isNew || hasChanged) {
                    hasBlisterChanges = true;
                    if (isNew) {
                      blisterChanges.push(`  • Nueva presentación: ${newBlister.seed_count} semillas - $${newBlister.price} (${newBlister.stock} unidades)`);
                    } else if (hasChanged) {
                      const changes = [];
                      if (oldBlister.price !== newBlister.price) {
                        changes.push(`precio: $${oldBlister.price} → $${newBlister.price}`);
                      }
                      if (oldBlister.stock !== newBlister.stock) {
                        changes.push(`stock: ${oldBlister.stock} → ${newBlister.stock}`);
                      }
                      blisterChanges.push(`  • ${newBlister.seed_count} semillas - ${changes.join(', ')}`);
                    }
                  }
                });
  
                // Solo agregar los cambios de blisters si realmente hubo cambios
                if (hasBlisterChanges) {
                  differences.push('\n📦 Cambios en presentaciones:');
                  differences.push(...blisterChanges);
                }
              } else {
                // Para otros campos mantener la lógica existente
                switch(key) {
                  case 'status':
                    differences.push(`🔄 ${fieldName}: "${statusTranslations[beforeValue] || beforeValue}" → "${statusTranslations[afterValue] || afterValue}"`);
                    break;
                  case 'price':
                    differences.push(`💰 ${fieldName}: $${beforeValue} → $${afterValue}`);
                    break;
                  case 'stock':
                    differences.push(`📦 ${fieldName}: ${beforeValue} → ${afterValue} unidades`);
                    break;
                  case 'description':
                    differences.push(`📝 Se modificó la ${fieldName.toLowerCase()}`);
                    break;
                  default:
                    if (beforeValue && afterValue) {
                      differences.push(`📝 ${fieldName}: "${beforeValue}" → "${afterValue}"`);
                    }
                }
              }
            }
          });
          
          return differences.length > 0 ? differences.join('\n') : 'No hay cambios detallados disponibles';
        }
        
        if (changes.old_status && changes.new_status) {
          return `🔄 Estado: "${statusTranslations[changes.old_status] || changes.old_status}" → "${statusTranslations[changes.new_status] || changes.new_status}"
    👤 Modificado por: ${changes.changed_by}
    💰 Total de la orden: $${changes.order_total}`;
        }
        
        return 'No hay cambios detallados disponibles';
      };
  
      const clearFilters = () => {
        filters.value = {
          action: '',
          date_from: '',
          search: ''
        };
        loadActivities();
      };
  
      const hasActiveFilters = computed(() => {
        return filters.value.action || filters.value.date_from || filters.value.search;
      });
  
      let searchTimeout;
      const debounceSearch = () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          loadActivities();
        }, 300);
      };
  
      const forceRefresh = async () => {
        try {
          isLoading.value = true;
          clearActivityCache();
          await Promise.all([
            loadActivities(true),
            loadStats()
          ]);
          toast.success('Actividades actualizadas');
        } catch (error) {
          console.error('Error refreshing activities:', error);
          toast.error('Error al actualizar las actividades');
        } finally {
          isLoading.value = false;
        }
      };
  
      // Agregar intervalo de actualización automática
      onMounted(() => {
        loadActivities();
        loadStats();
  
        // Actualizar actividades cada 30 segundos
        const refreshInterval = setInterval(() => {
          loadActivities(false);
        }, 30 * 1000);
  
        // Actualizar estadísticas cada 5 minutos
        const statsInterval = setInterval(() => {
          loadStats();
        }, 5 * 60 * 1000);
  
        // Limpiar intervalos al desmontar
        onUnmounted(() => {
          clearInterval(refreshInterval);
          clearInterval(statsInterval);
        });
      });
  
      return {
        activities,
        stats,
        isLoading,
        isLoadingMore,
        hasMoreActivities,
        filters,
        actionTypes,
        loadActivities,
        loadMore,
        formatDate,
        getActionIcon,
        getActionColor,
        getActionLabel,
        formatChanges,
        clearFilters,
        hasActiveFilters,
        debounceSearch,
        forceRefresh
      };
    }
  };
  </script>
  
  <style scoped>
  .stat-card {
    border: none;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }
  
  .hover-lift:hover {
    transform: translateY(-5px);
  }
  
  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .stat-icon.bg-primary { background: rgba(var(--primary-color-rgb), 0.1); }
  .stat-icon.bg-success { background: rgba(40, 167, 69, 0.1); }
  .stat-icon.bg-warning { background: rgba(255, 193, 7, 0.1); }
  .stat-icon.bg-info { background: rgba(23, 162, 184, 0.1); }
  
  .stat-icon i {
    font-size: 1.5rem;
  }
  
  .filter-card {
    background: var(--black-color-3);
    padding: 1.5rem;
    border-radius: 12px;
  }
  
  .search-wrapper {
    position: relative;
    flex: 1;
  }
  
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--white-color-2);
  }
  
  .search-input {
    padding-left: 2.5rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--white-color);
  }
  
  .activity-card {
    border-radius: 12px;
    overflow: hidden;
  }
  
  .timeline-wrapper {
    padding: 1.5rem;
  }
  
  .timeline-item {
    position: relative;
    padding-left: 3rem;
    padding-bottom: 2rem;
    border-left: 2px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
  }
  
  .timeline-item:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  
  .timeline-item.expanded {
    background: rgba(255, 255, 255, 0.03);
  }
  
  .timeline-icon {
    position: absolute;
    left: -1rem;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--black-color-2);
    z-index: 1;
  }
  
  .timeline-content {
    background: rgba(255, 255, 255, 0.05);
    padding: 1.25rem;
    border-radius: 12px;
  }
  
  .activity-title {
    color: var(--white-color);
    font-weight: 500;
  }
  
  .user-badge, .action-badge {
    font-size: 0.875rem;
    padding: 0.25rem 0.75rem;
    border-radius: 50px;
    background: rgba(255, 255, 255, 0.1);
    color: var(--black-color);
  }
  
  .action-badge {
    font-weight: 500;
  }
  
  .timestamp {
    font-size: 0.875rem;
    opacity: 0.7;
  }
  
  .changes-toggle {
    color: var(--black-color);
    font-size: 0.875rem;
  }
  
  .changes-toggle:hover {
    color: var(--primary-color);
    opacity: 0.8;
  }
  
  .changes-json {
    background: rgba(0, 0, 0, 0.2);
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    color: var(--white-color-2);
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    line-height: 1.6;
  }
  
  /* Agregar estilo para las líneas de cambios */
  .changes-json {
    white-space: pre-line;
    font-family: inherit;
  }
  
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  .skeleton {
    background: linear-gradient(
      90deg,
      var(--black-color-2) 0%,
      var(--black-color-3) 50%,
      var(--black-color-2) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite linear;
    border-radius: 4px;
  }
  
  .skeleton-item {
    opacity: 0;
    animation: fadeIn 0.3s ease forwards;
  }
  
  .skeleton-circle {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
  
  .skeleton-item:nth-child(1) { animation-delay: 0.1s; }
  .skeleton-item:nth-child(2) { animation-delay: 0.2s; }
  .skeleton-item:nth-child(3) { animation-delay: 0.3s; }
  .skeleton-item:nth-child(4) { animation-delay: 0.4s; }
  .skeleton-item:nth-child(5) { animation-delay: 0.5s; }
  
  @keyframes fadeIn {
    from { 
      opacity: 0;
      transform: translateY(10px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .timeline-content {
    background: var(--black-color-3);
  }
  
  .stat-card .skeleton {
    opacity: 0;
    animation: fadeIn 0.3s ease forwards, shimmer 1.5s infinite linear;
  }
  
  .stat-card:nth-child(1) .skeleton { animation-delay: 0.1s, 0s; }
  .stat-card:nth-child(2) .skeleton { animation-delay: 0.2s, 0s; }
  .stat-card:nth-child(3) .skeleton { animation-delay: 0.3s, 0s; }
  .stat-card:nth-child(4) .skeleton { animation-delay: 0.4s, 0s; }
  
  /* Asegurar que el stat-icon skeleton tenga las mismas dimensiones */
  .stat-icon.skeleton {
    width: 48px;
    height: 48px;
    background-color: var(--black-color-2);
  }
  
  @media (max-width: 768px) {
    .container {
      padding: 0 1rem;
    }
  
    .row {
      margin: 0;
    }
  
    .stat-card {
      margin-bottom: 1rem;
    }
  
    .filter-card {
      padding: 1rem;
    }
  
    .search-wrapper {
      width: 100%;
    }
  
    .d-flex.gap-3 {
      gap: 0.5rem !important;
    }
  
    .filter-card .d-flex {
      flex-direction: column;
      width: 100%;
    }
  
    .filter-card .d-flex > div {
      width: 100%;
      margin-bottom: 0.5rem;
    }
  
    .filter-card select,
    .filter-card input[type="date"] {
      width: 100%;
    }
  
    .timeline-wrapper {
      padding: 1rem;
    }
  
    .timeline-item {
      padding-left: 2rem;
      padding-bottom: 1.5rem;
    }
  
    .timeline-icon {
      width: 1.5rem;
      height: 1.5rem;
      left: -0.75rem;
    }
  
    .timeline-content {
      padding: 1rem;
    }
  
    .activity-title {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  
    .user-badge, 
    .action-badge {
      font-size: 0.75rem;
      padding: 0.2rem 0.5rem;
    }
  
    .timeline-content .d-flex {
      flex-direction: column;
      gap: 0.5rem;
    }
  
    .timestamp {
      font-size: 0.75rem;
      margin-top: 0.5rem;
    }
  
    .changes-json {
      font-size: 0.8rem;
      padding: 0.75rem;
      max-height: 150px;
    }
  
    .btn {
      width: 100%;
      margin-bottom: 0.5rem;
    }
  
    /* Ajustes específicos para los filtros en móvil */
    .d-flex.flex-wrap.gap-3 {
      flex-direction: column;
    }
  
    .d-flex.gap-3.flex-grow-1 {
      flex-direction: column;
      width: 100%;
    }
  
    .form-select,
    .date-range,
    .search-input {
      width: 100%;
      margin-bottom: 0.5rem;
    }
  
    /* Mejorar espaciado de las estadísticas */
    .stat-card .card-body {
      padding: 1rem;
    }
  
    .stat-icon {
      width: 40px;
      height: 40px;
    }
  
    .stat-icon i {
      font-size: 1.2rem;
    }
  
    .card-title.display-6 {
      font-size: 1.5rem;
    }
  
    /* Ajustes para los skeletons en móvil */
    .skeleton-text {
      height: 16px;
    }
  
    .skeleton-badge {
      height: 24px;
      width: 80px;
    }
  
    .stat-card {
      background: linear-gradient(145deg, var(--black-color-3), var(--black-color-2));
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      transition: all 0.3s ease;
      height: 100%;
    }
  
    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
    }
  
    .stat-card {
      margin-bottom: 0;
    }
  
    .stat-card .card-body {
      padding: 1rem;
    }
  
    .stat-icon {
      width: 36px;
      height: 36px;
    }
  
    .stat-icon i {
      font-size: 1rem;
    }
  
    .stat-title {
      font-size: 0.75rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  
    .stat-value {
      font-size: 1.25rem !important;
    }
  
    .row.g-3 {
      --bs-gutter-x: 0.75rem;
      --bs-gutter-y: 0.75rem;
      margin: 0 -0.375rem;
    }
  
    .col-6 {
      padding: 0 0.375rem;
    }
  
    .activity-header {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
    }
  
    .activity-title {
      font-size: 0.95rem;
      line-height: 1.4;
      margin-bottom: 0.5rem;
    }
  
    .user-badge, 
    .action-badge {
      font-size: 0.8rem;
      padding: 0.25rem 0.75rem;
    }
  
    .timestamp {
      font-size: 0.8rem;
      opacity: 0.8;
      margin: 0.25rem 0;
    }
  
    .timeline-content {
      padding: 1rem;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  
    .changes-toggle {
      margin-top: 0.75rem;
      padding: 0.5rem 1rem;
      width: auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  
    .changes-json {
      width: 100%;
      text-align: left;
    }
  }
  
  /* Ajustes específicos para pantallas muy pequeñas */
  @media (max-width: 375px) {
    .activity-title {
      font-size: 0.9rem;
    }
  
    .user-badge, 
    .action-badge {
      font-size: 0.75rem;
      padding: 0.2rem 0.6rem;
    }
  }
  </style>
  